import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/utils/api';

/**
 * Auth context for storing current user information
 */
const AuthContext = createContext(null);

/**
 * @typedef {Object} AuthUser
 * @property {string} id
 * @property {string} email
 * @property {string} [createdAt]
 * @property {string} [updatedAt]
 */

/**
 * @typedef {Object} AuthContextValue
 * @property {AuthUser|null} user
 * @property {boolean} isLoading
 * @property {boolean} isAuthenticated
 * @property {Function} login
 * @property {Function} logout
 * @property {Function} checkAuth
 */

/**
 * Custom hook to access auth context
 * @returns {AuthContextValue}
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * AuthProvider component that manages authentication state
 * @param {Object} props
 * @param {React.ReactNode} props.children
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = user !== null;

  /**
   * Get current user from /auth/me endpoint
   * @returns {Promise<AuthUser|null>}
   */
  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      setUser(null);
      setIsLoading(false);
      return null;
    }

    try {
      const response = await authAPI.me();
      if (response.data?.user) {
        setUser(response.data.user);
        return response.data.user;
      }
      setUser(null);
      return null;
    } catch (error) {
      // Token is invalid or expired
      console.debug('[Auth] Token validation failed:', error.message);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Login with email and password
   * @param {string} email
   * @param {string} password
   * @param {boolean} rememberMe - Keep user logged in for 7 days
   * @returns {Promise<AuthUser>}
   */
  const login = useCallback(async (email, password, rememberMe = false) => {
    const response = await authAPI.login(email, password, rememberMe);

    // Store tokens
    if (response.data?.token) {
      localStorage.setItem('auth_token', response.data.token);
    }
    if (response.data?.refreshToken) {
      localStorage.setItem('refresh_token', response.data.refreshToken);
    }

    // Store remember me preference and token expiry
    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
      // Calculate token expiry time (7 days from now)
      const expiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
      localStorage.setItem('token_expiry', expiryTime.toString());
    } else {
      localStorage.removeItem('rememberMe');
      // Calculate token expiry time (15 minutes from now)
      const expiryTime = Date.now() + (15 * 60 * 1000);
      localStorage.setItem('token_expiry', expiryTime.toString());
    }

    // Set user from response or fetch current user
    if (response.data?.user) {
      setUser(response.data.user);
      return response.data.user;
    }

    // Fetch current user if not returned
    return await checkAuth();
  }, [checkAuth]);

  /**
   * Logout and clear tokens
   */
  const logout = useCallback(() => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('rememberMe');
    localStorage.removeItem('token_expiry');
    setUser(null);
  }, []);

  // Check auth on mount and when token might have changed
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Auto-refresh token before expiry (for "Remember me" feature)
  useEffect(() => {
    const refreshInterval = setInterval(async () => {
      const rememberMe = localStorage.getItem('rememberMe') === 'true';
      const tokenExpiry = localStorage.getItem('token_expiry');
      const refreshToken = localStorage.getItem('refresh_token');

      if (!rememberMe || !tokenExpiry || !refreshToken) {
        return;
      }

      // Refresh if token expires in less than 5 minutes
      const expiryTime = parseInt(tokenExpiry);
      const timeUntilExpiry = expiryTime - Date.now();
      const fiveMinutes = 5 * 60 * 1000;

      if (timeUntilExpiry < fiveMinutes && timeUntilExpiry > 0) {
        try {
          console.debug('[Auth] Auto-refreshing token...');
          const response = await authAPI.refreshToken(refreshToken);

          if (response.data?.token) {
            localStorage.setItem('auth_token', response.data.token);
            // Update expiry time (7 days from now if remember me is enabled)
            const newExpiryTime = Date.now() + (7 * 24 * 60 * 60 * 1000);
            localStorage.setItem('token_expiry', newExpiryTime.toString());
            console.debug('[Auth] Token refreshed successfully');
          }
        } catch (error) {
          console.error('[Auth] Failed to refresh token:', error.message);
          // If refresh fails, logout user
          logout();
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(refreshInterval);
  }, [logout]);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;